import { Box, Card, Flex, Group, Stack, useMantineTheme } from '@mantine/core';
import type { PolicyTimeSeriesOut } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { SelectorTarget, SelectType } from '@repo/common/components';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../../api';
import type { DateRange } from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import DateRangeButtonGroup from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import { dateFilterOptions } from '../../constants';
import type { TimeRangeValue } from '../../types';
import { MetricsOverviewStackedLineChart } from './MetricsOverviewStackedLineChart';
import { processTimeseriesData } from './utils';

// Define the missing type
type PolicyChartDataPoint = {
	date: Date;
	ok: number;
	needsRemediation: number;
};

export const PoliciesOverviewChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
	}: {
		timeseries: PolicyTimeSeriesOut;
		dateFilter: string;
		setDateFilter: (dateFilter: string) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: string;
		setDateRangeOption: (dateRangeOption: string) => void;
	}) => {
		const { user, workspace } = useAuthUser();
		const theme = useMantineTheme();
		const chartData = useMemo(() => {
			if (!timeseries?.points) return [];

			const sortedData = processTimeseriesData(timeseries.points);

			const mappedData: PolicyChartDataPoint[] = sortedData.map((point) => {
				const data: PolicyChartDataPoint = {
					date: point.timestamp,
					ok: point.ok,
					needsRemediation: point.needs_remediation,
				};

				return data;
			});

			return mappedData;
		}, [timeseries]);

		const channels = useMemo(
			() => [
				{
					label: 'OK',
					x: 'date' as keyof (typeof chartData)[0],
					y: 'ok' as keyof (typeof chartData)[0],
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/success/default'),
					zIndex: 10,
				},
				{
					label: 'Needs remediation',
					x: 'date' as keyof (typeof chartData)[0],
					y: 'needsRemediation' as keyof (typeof chartData)[0],
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/warning/default'),
					zIndex: 20,
				},
			],
			[]
		);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : (
								point.date.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[0].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Ok </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.ok}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[1].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Needs remediation </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.needsRemediation}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[chartData, dateRangeOption]
		);

		const handleDateFilterChange = useCallback(
			(values: string[]) => {
				const newDateFilter = values[0] as string;
				setDateFilter(newDateFilter);
				setTimeRangeValue({
					...timeRangeValue,
					timeRange: newDateFilter,
				});
				trackEvent(
					'policies/overview/date_filter_change',
					{
						date_filter: newDateFilter,
					},
					user,
					workspace!
				);
			},
			[setDateFilter, setTimeRangeValue, timeRangeValue, user, workspace]
		);

		const handleDateRangeChange = useCallback(
			(range: DateRange, optionId: string) => {
				setTimeRangeValue({
					timeRange: dateFilter,
					dateRange: range,
				});
				setDateRangeOption(optionId);
				trackEvent(
					'policies/overview/date_range_change',
					{
						date_range_option: optionId,
						date_range_from: range.from?.toISOString() ?? '',
						date_range_to: range.to?.toISOString() ?? '',
					},
					user,
					workspace!
				);
			},
			[dateFilter, setTimeRangeValue, setDateRangeOption, user, workspace]
		);

		const getDateFilterItems = useCallback(async () => dateFilterOptions, []);

		const selectedDateFilterLabel = useMemo(
			() =>
				dateFilterOptions.find((item) => item.value === dateFilter)?.label ??
				'Last 24 hours',
			[dateFilter]
		);

		return (
			<Card withBorder style={{ borderRadius: theme.radius.md }}>
				<Stack spacing="md">
					<Group position="apart">
						<Title size="sm" weight="bold">
							Framework progress
						</Title>
						<Group position="apart" spacing="sm">
							<Selector
								initialValue={[dateFilter]}
								getItems={getDateFilterItems}
								onChange={handleDateFilterChange}
								selectType={SelectType.Single}
								disableSearch
							>
								<SelectorTarget size="md" variant="default">
									{selectedDateFilterLabel}
								</SelectorTarget>
							</Selector>
							<DateRangeButtonGroup
								selectedOptionId={dateRangeOption}
								onChange={handleDateRangeChange}
							/>
						</Group>
					</Group>
					<MetricsOverviewStackedLineChart
						channels={channels}
						chartData={chartData}
						dateFilter={dateFilter}
						tooltipDataRenderer={tooltipDataRenderer}
						dateRangeOption={dateRangeOption}
					/>
				</Stack>
			</Card>
		);
	}
);
